/**
 * The scope, the period arithmetic and the RPC row shape — shared by every
 * `mediaIntelligence*` module.
 *
 * WHY THIS FILE EXISTS AT ALL, since it is not a concern anybody would name:
 * it breaks an import CYCLE. `mediaIntelligenceService` is being split up, and
 * the MMM half needs `Scope`, `scoped`, `periodToRange` and `ALL_TIME_START`
 * while the service needs the MMM half's two fetchers for its default-export
 * barrel. Left in the service those two facts are a cycle; moved down here they
 * are a layer. Nothing above imports the service, and the service imports
 * downward only.
 *
 * EXTRACTED, NOT REDESIGNED: same declarations, same comments. `scoped`,
 * `ALL_TIME_START` and `CampaignSummaryRow` were module-private in the service
 * and are exported here because more than one module now needs them — that is
 * the only edit.
 */
import { getSupabaseClient, isSupabaseConfigured } from '@/config';
import { todayDataDate, shiftDataDate } from './dataDate';
import { info, logError } from './logger';
import { readAllRows, type PagedResult } from './supabasePaging';
import {
  loadStaticDemoData,
  USE_STATIC_DEMO_DATA,
  type StaticDemoDataJson,
} from './demoDataService';

/**
 * Which workspace(s) a data query is scoped to. MANDATORY on every fetcher —
 * no default — so the compiler flags any call site that forgets to pass it
 * (the RLS union that this replaces was itself an invisible ambient default).
 * An array from the start so combining workspaces later needs no call-site churn.
 */
export type Scope = { workspaceIds: string[] };

// The single place workspace filtering is written. Every .from(...).select(...)
// gets .in('workspace_id', ...) via this helper. The generic `T -> T` signature
// preserves each call site's own builder/result type; `any` is confined to the
// cast on the next line only (the direct generic constraint form hits
// TS2589 "type instantiation is excessively deep" against the supabase-js
// builder types).
export const scoped = <T>(query: T, scope: Scope): T =>
  (query as { in(col: string, values: readonly string[]): T }).in('workspace_id', scope.workspaceIds);

/**
 * Earliest date the 'all' window reaches back to. A fixed constant rather than a
 * MIN(date) lookup so periodToRange stays synchronous and free of I/O — every
 * caller below is on a hot path, and no ad platform in this product predates it.
 */
export const ALL_TIME_START = '2000-01-01';

// Rolling window for the period selector: week = last 7 days, month = 30, quarter
// = 90 (matches the "Last N Days" labels the UI shows), all = everything on
// record. Inclusive of today. This is the ONE place the period → date-range
// mapping lives; every fetch below uses it so the cards, channels, and
// drill-downs all filter the identical window and their totals reconcile.
//
// 'all' exists because the rolling windows can miss the data entirely: a
// workspace whose last sync landed weeks ago has nothing inside "last 7 days"
// and a sliver inside "last 30". It widens the same shared window rather than
// bypassing it, so that reconciliation property still holds.
export const periodToRange = (period = 'month'): { start: string; end: string } => {
  // Data-calendar today (Asia/Bangkok), not UTC: with toISOString() the whole
  // window lagged a day between midnight and 07:00 Bangkok (audit F3).
  const end = todayDataDate();
  if (period === 'all') return { start: ALL_TIME_START, end };

  const days = period === 'week' ? 7 : period === 'quarter' ? 90 : 30;
  return { start: shiftDataDate(end, -(days - 1)), end };
};

/**
 * The window immediately before the selected one, of the same length.
 *
 * Lives beside periodToRange deliberately: the two must agree on how long a
 * period is, and a second copy of `week = 7, quarter = 90, else 30` is how a
 * comparison quietly starts measuring 30 days against 31.
 *
 * Null for 'all' — nothing precedes everything on record, and inventing a
 * baseline there would produce a percentage with no meaning.
 */
export const previousPeriodRange = (period = 'month'): { start: string; end: string } | null => {
  if (period === 'all') return null;

  const current = periodToRange(period);
  const days = period === 'week' ? 7 : period === 'quarter' ? 90 : 30;
  // Ends the day before the current window opens, so the two never overlap.
  const end = shiftDataDate(current.start, -1);
  return { start: shiftDataDate(end, -(days - 1)), end };
};

/** Just the two figures a period-over-period comparison needs. */
export interface PeriodTotals {
  spend: number;
  conversions: number;
}

/**
 * Spend and conversions for an explicit window.
 *
 * Same RPC and the same `platform !== 'analytics'` exclusion as
 * fetchCampaignMetrics, on purpose: a comparison assembled from a different
 * aggregation would produce a percentage that disagrees with the number it
 * sits under, and the reader has no way to tell which of the two is lying.
 *
 * @returns the totals, or null when they could not be read — never zeros,
 *   which in this codebase mean "measured, and it was nothing"
 */
export const fetchPeriodTotals = async (
  scope: Scope,
  range: { start: string; end: string },
): Promise<PeriodTotals | null> => {
  if (!isSupabaseConfigured()) return null;

  try {
    const { rows: data, error } = await readCampaignSummary(
      scope, range.start, range.end, 'fetchPeriodTotals',
    );
    if (error) {
      logError('MediaIntelligenceService', 'Error fetching period totals', error);
      return null;
    }

    const rows = data.filter((r) => r.platform !== 'analytics');
    return {
      spend: rows.reduce((sum, row) => sum + Number(row.total_spend || 0), 0),
      conversions: rows.reduce((sum, row) => sum + (row.total_conversions || 0), 0),
    };
  } catch (err) {
    logError('MediaIntelligenceService', 'Period totals failed', err);
    return null;
  }
};

// Shape of a get_campaign_summary(...) row — the RPC isn't in the generated
// Supabase types, so its result is untyped; this restores type safety at the
// call sites (the SUM columns come back as JS numbers).
export interface CampaignSummaryRow {
  id: string;
  workspace_id: string;
  platform: string;
  campaign_name: string;
  status: string;
  objective: string;
  start_date: string | null;
  end_date: string | null;
  total_spend: number;
  total_impressions: number;
  total_clicks: number;
  total_conversions: number;
  total_value: number;
  // Added by migration 037. Optional so a client running against a database
  // that has not taken the migration yet degrades to 0 rather than NaN.
  total_reach?: number;
  total_engagements?: number;
  total_video_views?: number;
}

/**
 * Every campaign the scope can see, in one list, for an explicit window.
 *
 * THE ONE READER for `get_campaign_summary`. Six call sites used to issue this
 * RPC themselves, all unpaged, and PostgREST answers an unpaged read with at
 * most 1000 rows, HTTP 200 and no error — so the day a workspace crosses 1000
 * campaigns every total on the dashboard, the channel table, the MMM
 * recommendation and the Web Analytics card would quietly start shrinking, each
 * by a different amount depending on which rows happened to land in the first
 * page. `media_campaigns` holds 621 rows today (XPeng 489), so the cap is not
 * hit yet; this is here so it never is.
 *
 * ORDERED BY `id`, and that is load-bearing. The function is `GROUP BY c.id`
 * with no ORDER BY of its own, so without one PostgREST pages an unordered
 * result and the database is free to order rows differently per page — which
 * drops some and repeats others at the boundaries. `id` is unique, so it needs
 * no tie-break.
 *
 * MEASURED against the local API before this was written (XPeng, all-time):
 * `?workspace_id=in.(…)&order=id.asc&offset=&limit=` walked in pages of 100
 * returned 489 rows, 489 unique, 0 duplicates, and spend summing to
 * ฿6,300,597.96 — the cent-for-cent total of one whole read. Note the mechanism
 * is `offset`/`limit` query params, which is what supabase-js `.range()` sends;
 * a `Range:` header is ignored by this stack and proves nothing.
 *
 * Returns rows AND error, never throwing: only the call site knows whether a
 * short list may be shown as though it were complete.
 */
export const readCampaignSummary = (
  scope: Scope,
  start: string,
  end: string,
  label: string,
): Promise<PagedResult<CampaignSummaryRow>> =>
  readAllRows<CampaignSummaryRow>(
    (from, to) =>
      scoped(
        getSupabaseClient()
          .rpc('get_campaign_summary', { p_start: start, p_end: end })
          .order('id', { ascending: true })
          .range(from, to),
        scope,
      ),
    label,
  );


// ---------------------------------------------------------------------------
// The static demo JSON, and the cache that keeps every caller on ONE copy.
// Down here rather than in the service because both halves of the split read
// it — the comprehensive fetch and the MMM mock pipeline — and a module-level
// cache read through two different import paths is two caches.
// ---------------------------------------------------------------------------
// Cache for static demo data to ensure consistency across all service calls
let cachedStaticData: StaticDemoDataJson | null = null;
let staticDataLoadPromise: Promise<StaticDemoDataJson | null> | null = null;

/**
 * Get static demo data for the default scenario
 * Uses cache to ensure consistency across multiple calls
 */
export async function getStaticDemoData(): Promise<StaticDemoDataJson | null> {
  // Return cached data if available
  if (cachedStaticData) {
    return cachedStaticData;
  }

  // If a load is in progress, wait for it
  if (staticDataLoadPromise) {
    return staticDataLoadPromise;
  }

  // Load static data
  if (USE_STATIC_DEMO_DATA) {
    staticDataLoadPromise = loadStaticDemoData();
    const data = await staticDataLoadPromise;

    if (data) {
      cachedStaticData = data;
      info('MediaIntelligenceService', 'Using cached static demo data for media intelligence service');
    } else {
      // Clear the in-flight marker on failure. It exists to stop a second
      // caller starting a duplicate load, not to remember that loading failed:
      // held past a failure, the `if (staticDataLoadPromise) return it` above
      // handed every later call the same settled null, so one transient miss
      // turned demo mode off for the rest of the session.
      staticDataLoadPromise = null;
    }

    return data;
  }

  return null;
}

/**
 * Clear the static demo data cache
 * Call this to force a reload of the JSON data
 */
export function clearStaticDemoDataCache(): void {
  cachedStaticData = null;
  staticDataLoadPromise = null;
  info('MediaIntelligenceService', 'Cleared static demo data cache in media intelligence service');
}
